/* eslint-disable react/no-unescaped-entities, react-hooks/set-state-in-effect --
 * v77.99.77: Idiomatic React patterns (effect-driven state hydration,
 * English text with apostrophes/quotes in JSX). These experimental
 * rules are not shipped by eslint-plugin-react-hooks 5.2.0 and
 * mis-flag intentional patterns. `react/no-unescaped-entities`
 * similarly flags safe English text.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import TurnstileWidget from "@/components/TurnstileWidget";
import { CheckCircle2, MailCheck } from "lucide-react";

/**
 * Verify-email pages.
 *
 * - `VerifyEmail` (route: `/verify-email?token=…`): consumes a token
 *   from the email link. On success: shows a success card, refreshes
 *   the auth context (so `is_email_verified` flips to true) and offers
 *   a "Go to dashboard" CTA. On failure: shows the failure reason
 *   (invalid / expired) plus a "request a new link" form.
 *
 * - `VerifyPending` (route: `/verify-pending`): the screen a brand-
 *   new parent is sent to right after registration. Tells them to
 *   check their email + lets them resend the verification link
 *   (Turnstile-gated to prevent resend spam).
 */
function ResendForm() {
  const turnstileEnabled = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const [email, setEmail] = useState("");
  const [tsToken, setTsToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const tsRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (turnstileEnabled && !tsToken) {
      toast.error(
        "Verification not ready",
        {
          description: "Please tick the 'Verify you are human' box before sending. If you can't see it, refresh the page or check that your network isn't blocking challenges.cloudflare.com.",
        },
      );
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/resend-verification", {
        email,
        turnstile_token: tsToken,
      });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message) || (typeof detail === "string" ? detail : "Failed");
      toast.error(msg);
      try { tsRef.current?.reset(); setTsToken(""); } catch (e) { console.debug("[verify-email] turnstile reset failed:", e?.message); }
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="text-sm text-[#2E3A3F] border border-[#D9E1E3] bg-[#EEF3F4] rounded-sm p-4" data-testid="resend-sent-message">
        If an unverified account exists for that email, a fresh verification link has been sent. Please check your inbox and spam folder.
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="resend-verification-form">
      <div>
        <Label htmlFor="resend-email">Email</Label>
        <Input
          id="resend-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          data-testid="resend-email-input"
        />
      </div>
      <TurnstileWidget
        ref={tsRef}
        action="resend-verification"
        testId="resend-turnstile"
        onToken={setTsToken}
      />
      {turnstileEnabled && !tsToken && (
        <p className="text-[11px] text-[#6F828A]" data-testid="resend-turnstile-hint">
          Tick the verification box above to enable sending.
        </p>
      )}
      <Button
        type="submit"
        disabled={busy || (turnstileEnabled && !tsToken)}
        className="w-full bg-[#1F9FA4] hover:bg-[#178085] rounded-sm"
        data-testid="resend-submit-button"
      >
        {busy ? "Sending…" : "Resend verification email"}
      </Button>
    </form>
  );
}

function PageShell({ children, testId }) {
  return (
    <div
      className="min-h-screen bg-[#F5F7F8] dark:bg-[#0F1517] text-[#2E3A3F] dark:text-[#E8EEF0] flex flex-col"
      data-testid={testId}
    >
      <header className="border-b border-[#D9E1E3] dark:border-[#2A3539] bg-white dark:bg-[#0F1517]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="verify-brand">
            <Logo size={32} />
            <span className="font-heading font-semibold text-lg">CoParent Console</span>
          </Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md border border-[#D9E1E3] bg-white rounded-md p-6 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}

export function VerifyPending() {
  const { user, logout } = useAuth();
  const email = user?.email || "";
  return (
    <PageShell testId="verify-pending-page">
      <div className="flex items-center gap-2 text-[#1F9FA4]">
        <MailCheck className="w-6 h-6" />
        <h1 className="font-heading text-2xl font-semibold">Check your inbox</h1>
      </div>
      <p className="text-sm text-[#6F828A] mt-2">
        We've sent a verification link to{" "}
        <span className="font-mono-data text-[#2E3A3F]">{email || "your email"}</span>.
        Please click the link to activate your CoParent Console account.
        The link expires in 24 hours.
      </p>
      <p className="text-xs text-[#6F828A] mt-2">
        Didn't get it? Check your spam folder or request a new link below.
      </p>
      <div className="mt-5">
        <ResendForm />
      </div>
      <div className="mt-6 text-center">
        <button
          onClick={() => { logout(); }}
          className="text-xs text-[#6F828A] hover:text-[#2E3A3F]"
          data-testid="verify-pending-signout"
        >
          Sign out and use a different email
        </button>
      </div>
    </PageShell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ status: "verifying", message: "" });
  const navigate = useNavigate();
  const { refresh } = useAuth();

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ status: "error", message: "This link is missing a verification token." });
      return;
    }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        if (cancelled) return;
        setState({ status: "ok", message: "" });
        // Best-effort refresh — the user record now has
        // is_email_verified=true and the session token is unchanged.
        try { await refresh?.(); } catch (e) { console.debug("[verify-email] post-verify refresh failed:", e?.message); }
      } catch (err) {
        if (cancelled) return;
        const detail = err.response?.data?.detail;
        const msg = (detail && typeof detail === "object" && detail.message) || "Verification failed.";
        setState({ status: "error", message: msg });
      }
    })();
    return () => { cancelled = true; };
  }, [token, refresh]);

  if (state.status === "verifying") {
    return (
      <PageShell testId="verify-email-verifying">
        <h1 className="font-heading text-2xl font-semibold">Verifying…</h1>
        <p className="text-sm text-[#6F828A] mt-1">Please hold on.</p>
      </PageShell>
    );
  }

  if (state.status === "ok") {
    return (
      <PageShell testId="verify-email-success">
        <div className="flex items-center gap-2 text-[#1F9FA4]">
          <CheckCircle2 className="w-6 h-6" />
          <h1 className="font-heading text-2xl font-semibold">Email verified</h1>
        </div>
        <p className="text-sm text-[#6F828A] mt-2">
          Your CoParent Console account is now active. Welcome aboard.
        </p>
        <Button
          onClick={() => navigate("/app/dashboard")}
          className="mt-5 w-full bg-[#1F9FA4] hover:bg-[#178085] rounded-sm"
          data-testid="verify-go-dashboard-button"
        >
          Go to dashboard
        </Button>
      </PageShell>
    );
  }

  return (
    <PageShell testId="verify-email-error">
      <h1 className="font-heading text-2xl font-semibold">Verification failed</h1>
      <p className="text-sm text-[#6F828A] mt-2" data-testid="verify-email-error-message">
        {state.message}
      </p>
      <p className="text-sm text-[#6F828A] mt-4">
        Request a fresh verification link below:
      </p>
      <div className="mt-3">
        <ResendForm />
      </div>
    </PageShell>
  );
}

export default VerifyEmail;
